import { useMemo } from "react";
import * as geojson from "geojson";
import { useLayerStale } from "../../hooks/useLayerStale";
import { Layer } from "../../types/layer";

const WORLD: geojson.Position[] = [[-180, -90], [-180, 90], [180, 90], [180, -90], [-180, -90]];

/** The area's polygons, one or many ("Whole project" can be multipart). */
export const areaParts = (g: geojson.Geometry | null | undefined): geojson.Position[][][] =>
  g?.type === "Polygon" ? [g.coordinates] : g?.type === "MultiPolygon" ? g.coordinates : [];

/** Dims the map outside the traced area and outlines it, so the photo
 *  picked for fine detail is judged inside the area and the two sources
 *  visibly differ even where one of them is the basemap itself. */
const DataReviewAreaMaskLayer = (id: string, area: geojson.Feature | null, visible: boolean): Layer => {
  const g = area?.geometry;
  const staleOf = useLayerStale();
  const source = useMemo<geojson.FeatureCollection>(() => {
    const parts = areaParts(g);
    return {
      type: "FeatureCollection",
      features: parts.length
        ? [
            // The world with every part cut out of it, and the parts' own
            // holes (outside the area too) dimmed again.
            {
              type: "Feature", properties: { part: "mask" },
              geometry: { type: "MultiPolygon", coordinates: [[WORLD, ...parts.map((p) => p[0])], ...parts.flatMap((p) => p.slice(1).map((hole) => [hole]))] },
            },
            { type: "Feature", properties: { part: "edge" }, geometry: g as geojson.Polygon | geojson.MultiPolygon },
          ]
        : [],
    };
  }, [g]);
  return {
    id,
    name: "Area to look at",
    type: "data-review-mask",
    source,
    layerStyle: [
      { id, type: "fill", filter: ["==", ["get", "part"], "mask"], paint: { "fill-color": "#0b1118", "fill-opacity": 0.62 } },
      { id: `${id}-edge`, type: "line", filter: ["==", ["get", "part"], "edge"], paint: { "line-color": "#00d4ad", "line-width": 2.5 } },
    ],
    toolbarButton: null,
    active: visible && source.features.length > 0,
    ...staleOf(id, source),
  };
};

export default DataReviewAreaMaskLayer;
